import { useState, useContext, useEffect, useRef } from "react";
import axios from "axios";
import { AuthContext } from "../../../../../contexts/AuthContext";
import "./MaqPagamentosAdm.css";
import CustomDateRangePicker from "../CustomDatePicker/CustomRangePicker";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import * as links from "../../../../../utils/links";
import PagamentosTable from "../Table/PagamentosTable";

const dinheiro = (valor) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(valor || 0));

export default function MaqPagamentosAdm() {
  const location = useLocation();
  const estado = location.state || {};
  const maquinaInfos = estado.maquinaInfos || {};
  const clienteInfo = estado.clienteInfo || {};
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const dataUser = authInfo?.dataUser;
  const navigate = useNavigate();
  const token = dataUser?.token;
  const { id } = useParams();

  const [estornos, setEstornos] = useState(0);
  const [estoque, setEstoque] = useState("");
  const [cash, setCash] = useState(0);
  const [total, setTotal] = useState(0);
  const [listCanals, setListCanals] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [periodoLoading, setPeriodoLoading] = useState(false);
  const [periodoErro, setPeriodoErro] = useState("");
  const periodoRequestRef = useRef(0);
  const [dataInicio, setDataInicio] = useState(null);
  const [dataFim, setDataFim] = useState(null);

  const headers = {
    "x-access-token": token,
    "content-type": "application/json",
  };

  const tratarErro = (err) => {
    if ([401, 403].includes(err?.response?.status)) {
      setNotiMessage({ type: "error", message: "A sua sessão expirou, para continuar faça login novamente." });
      navigate(dataUser?.key === "ADMIN" ? links.ADMIN_SIGNIN : links.SIGNIN);
      setDataUser(null);
      return true;
    }
    return false;
  };

  const aplicarResposta = (res) => {
    setEstornos(res?.data?.estornos || 0);
    setCash(res?.data?.cash || 0);
    setEstoque(res?.data?.estoque ?? "-");
    setTotal(res?.data?.total || 0);
    setListCanals(Array.isArray(res?.data?.pagamentos) ? res.data.pagamentos : []);
  };

  const getData = async (maquinaId = id) => {
    if (!maquinaId) return;
    periodoRequestRef.current += 1;
    setPeriodoLoading(false);
    setPeriodoErro("");
    setLoadingTable(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_SERVIDOR}/pagamentos-adm/${maquinaId}`, { headers });
      aplicarResposta(res);
    } catch (err) {
      tratarErro(err);
    } finally {
      setLoadingTable(false);
    }
  };

  const getPaymentsPeriod = async (inicio, fim) => {
    if (!id || !inicio || !fim) return;

    const requisicaoAtual = ++periodoRequestRef.current;
    setPeriodoLoading(true);
    setPeriodoErro("");

    try {
      const body = {
        maquinaId: id,
        // Exatamente a mesma janela usada pelo Rel. de Pagamentos.
        dataInicio: `${inicio}T00:00:00.000Z`,
        dataFim: `${fim}T23:59:00.000Z`,
      };

      const periodoVal = await axios.post(
        `${process.env.REACT_APP_SERVIDOR}/pagamentos-periodo-adm/${id}`,
        {
          dataInicio: body.dataInicio,
          dataFim: body.dataFim,
        },
        { headers }
      );

      if (requisicaoAtual !== periodoRequestRef.current) return;

      // Usa a mesma consulta consolidada do backend para pagamentos e estornos.
      // Essa rota considera o estorno real registrado no pagamento/entrega,
      // inclusive quando o estorno foi confirmado por status e não apenas pelo
      // boolean antigo "estornado".
      setTotal(Number(periodoVal?.data?.total || 0));
      setEstornos(Number(periodoVal?.data?.estornos || 0));
      setCash(Number(periodoVal?.data?.cash || 0));
    } catch (err) {
      if (requisicaoAtual !== periodoRequestRef.current) return;
      if (!tratarErro(err)) {
        setPeriodoErro("Não foi possível consultar os valores deste período. Tente novamente.");
      }
    } finally {
      if (requisicaoAtual === periodoRequestRef.current) {
        setPeriodoLoading(false);
      }
    }
  };

  // SCROLL_MACHINE_TOP_2026
  useEffect(() => {
    const voltarAoTopo = () => {
      const conteudo = document.querySelector(".Main_content");
      if (conteudo) {
        if (typeof conteudo.scrollTo === "function") {
          conteudo.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
        conteudo.scrollTop = 0;
        conteudo.scrollLeft = 0;
      }

      if (typeof window.scrollTo === "function") {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    voltarAoTopo();
    const frame = window.requestAnimationFrame(voltarAoTopo);
    const timer = window.setTimeout(voltarAoTopo, 50);

    getData(id);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [id]);

  useEffect(() => {
    if (dataInicio && dataFim) getPaymentsPeriod(dataInicio, dataFim);
  }, [dataInicio, dataFim, id]);

  const valorResumo = (valor) => {
    if (periodoLoading) return "Consultando...";
    if (periodoErro) return "Erro";
    return dinheiro(valor);
  };

  const onRelatorioHandler = () => {
    if (!dataInicio || !dataFim) {
      setNotiMessage({ type: "error", message: "Selecione no calendário a data de início e fim para gerar o relatório desta máquina." });
      return;
    }
    navigate(`${links.RELATORIO_ADMIN}/${id}`, { state: { maquinaInfos, clienteInfo, dataInicio, dataFim } });
  };

  const onRelatorioDispHandler = () => {
    navigate(`${links.RELATORIO_DISP_ADMIN}/${id}`, { state: { maquinaInfos, clienteInfo, dataInicio, dataFim } });
  };

  return (
    <div className="machine-detail-page">
      <div className="machine-detail-titlebar">
        <div>
          <span className="machine-detail-kicker">MÁQUINA</span>
          <h1>{maquinaInfos?.nome || "Máquina"}</h1>
          <small>{maquinaInfos?.descricao || "Detalhes e pagamentos"}</small>
        </div>
        <button
          className="machine-action-btn action-back"
          type="button"
          onClick={() => navigate(`${links.CLIENTES_MAQUINAS}/${clienteInfo.id}`, { state: clienteInfo })}
        >
          <i className="bi bi-arrow-left" /> Voltar
        </button>
      </div>

      <div className="machine-detail-toolbar">
        <div className="machine-date-filter">
          <CustomDateRangePicker setDataFim={setDataFim} setDataInicio={setDataInicio} resetSearch={getData} id={id} />
        </div>
        <button className="machine-action-btn action-edit" type="button" onClick={() => navigate(`${links.CLIENTES_MAQUINAS_EDIT_FORNECEDOR}/${id}`, { state: estado })}>
          <i className="bi bi-pencil" /> Editar Máquina
        </button>
        <button className="machine-action-btn action-credit" type="button" onClick={() => navigate(`${links.CREDITO_REMOTO_ADM}/${maquinaInfos.id || id}`, { state: estado })}>
          <i className="bi bi-credit-card" /> Crédito Remoto
        </button>
        <button
          className="machine-action-btn action-qr"
          type="button"
          onClick={() => maquinaInfos?.store_id && window.open(`//www.mercadopago.com.br/stores/detail?store_id=${maquinaInfos.store_id}`, "_blank")}
          disabled={!maquinaInfos?.store_id}
        >
          <i className="bi bi-qr-code" /> QR Code
        </button>
        <button className="machine-action-btn action-report" type="button" onClick={onRelatorioHandler}>
          <i className="bi bi-file-earmark-text" /> Rel. de Pagamentos
        </button>
        <button className="machine-action-btn action-availability" type="button" onClick={onRelatorioDispHandler}>
          <i className="bi bi-file-bar-graph" /> Rel. de Disponibilidade
        </button>
        <button className="machine-action-btn action-id" type="button" onClick={() => navigate(`${links.CLIENTES_MAQUINAS_TROCAR}/${id}`, { state: estado })}>
          <i className="bi bi-arrow-counterclockwise" /> Trocar ID
        </button>
        <button className="machine-action-btn action-delete action-reset-acerto" type="button" onClick={() => navigate(`${links.CLIENTES_MAQUINA_DELETE_PAGAMENTOS}/${id}`, { state: estado })}>
          <i className="bi bi-arrow-repeat" /> Zerar Acerto do Mês
        </button>
      </div>

      {periodoErro && (
        <div className="machine-period-error" role="alert">
          <i className="bi bi-exclamation-triangle" /> {periodoErro}
        </div>
      )}

      <div className="machine-stat-grid">
        <div className="machine-stat-card stat-total"><span>Total</span><strong className={periodoLoading ? "is-loading" : periodoErro ? "is-error" : ""}>{valorResumo(total)}</strong></div>
        <div className="machine-stat-card stat-refund"><span>Estornos</span><strong className={periodoLoading ? "is-loading" : periodoErro ? "is-error" : ""}>{valorResumo(estornos)}</strong></div>
        <div className="machine-stat-card stat-cash"><span>Espécie</span><strong className={periodoLoading ? "is-loading" : periodoErro ? "is-error" : ""}>{valorResumo(cash)}</strong></div>
        <div className="machine-stat-card stat-pulse"><span>Pulso</span><strong>{dinheiro(maquinaInfos?.pulso)}</strong></div>
        <div className="machine-stat-card"><span>Store ID</span><strong>{maquinaInfos?.store_id || "-"}</strong></div>
        <div className="machine-stat-card"><span>Maquininha</span><strong>{maquinaInfos?.maquininha_serial || "-"}</strong></div>
        <div className="machine-stat-card"><span>Estoque</span><strong>{estoque === "" ? "-" : estoque}</strong></div>
      </div>

      <section className="machine-payments-card">
        <div className="machine-payments-header">
          <div>
            <span>PAGAMENTOS</span>
            <h2>{maquinaInfos?.nome || "Máquina"}</h2>
          </div>
          <small>{maquinaInfos?.descricao || ""}</small>
        </div>
        <div className="machine-payments-table">
          <PagamentosTable listCanals={listCanals} loading={loadingTable} />
        </div>
      </section>
    </div>
  );
}
